import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useModal } from '../../contexts/ModalContext';
import { navigateTo } from '../../utils';
import { KEYSTONE_REDIRECT_URL, NGW_REDIRECT_URL } from '../../config';
import { PropertyField } from '../../system/field';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { useTenantPath } from '../../hooks/useTenantPath';
import {
  ListingResource,
  useListingActions,
  useListingField,
  useListingMetadata,
} from '@brightmls/nextgen.lae.flc';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const WARNING_THRESHOLD = 10 * 60 * 1000; // 10 minutes

const formatTime = (timeInMs: number) => {
  const minutes = Math.floor(timeInMs / 60000);
  const seconds = Math.floor((timeInMs % 60000) / 1000);
  return {
    minutes,
    seconds: seconds.toString().padStart(2, '0'),
  };
};

const CountdownTimer = ({
  initialTimeLeft,
  onExpire,
}: {
  initialTimeLeft: number;
  onExpire: () => void;
}) => {
  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  const { minutes, seconds } = formatTime(timeLeft);
  const progress = (timeLeft / WARNING_THRESHOLD) * 100;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          clearInterval(intervalId);
          onExpire();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [onExpire]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: 200,
        height: 200,
        margin: '24px auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress
        variant="determinate"
        value={progress}
        size={200}
        thickness={4}
        sx={{
          position: 'absolute',
          color: 'error.medium',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          },
        }}
      />
      <Box
        sx={{
          textAlign: 'center',
          position: 'absolute',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 'bold',
            color: '#1A1A1E',
            fontSize: '48px',
            lineHeight: 1,
          }}
        >
          {minutes}:{seconds}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Typography
            sx={{
              color: '#1A1A1E',
              fontSize: '14px',
              textTransform: 'uppercase',
            }}
          >
            MINUTES
          </Typography>
          <Typography
            sx={{
              color: '#1A1A1E',
              fontSize: '14px',
              textTransform: 'uppercase',
            }}
          >
            SECONDS
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ListingInactivityWarning = () => {
  const { discardListing } = useListingActions();
  const { isFromLiveListing } = useListingMetadata();
  const { showModal, hideModal } = useModal();
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const { hasFeature } = useFeatureFlags();
  const { getPath } = useTenantPath();

  const address = useListingField(
    ListingResource.Property,
    PropertyField.UNPARSED_ADDRESS,
    '',
  );

  // Get the appropriate overview URL based on feature flag
  const getOverviewUrl = () => {
    const showOverview = hasFeature(FeatureFlags.NGW_OVERVIEW);
    return showOverview ? getPath(NGW_REDIRECT_URL) : KEYSTONE_REDIRECT_URL;
  };

  const resetTimer = () => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  };

  const handleExtendSession = () => {
    hideModal();
    resetTimer();
  };

  const showExpirationModal = () => {
    discardListing();

    const overview = () => {
      navigateTo(getOverviewUrl());
    };

    showModal({
      title: `EXPIRED - ${address.value}`,
      content: (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Box sx={{ my: 3 }}>
            <img
              src="/icons/warning.svg"
              alt="Warning"
              width={120}
              height={120}
            />
          </Box>
          <Typography variant="body1">
            Changes to the listing have been lost as you have been inactive too
            long.
          </Typography>
        </Box>
      ),
      onConfirm: () => {
        overview();
        hideModal();
      },
      onDismiss: () => {
        overview();
      },
      confirmText: 'CLOSE',
    });
  };

  const handleInactivityWarning = () => {
    showModal({
      title: `INACTIVITY WARNING - ${address.value}`,
      content: (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Listings lose their changes if they are inactive for 60 minutes.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your changes will be discarded in
          </Typography>
          <CountdownTimer
            initialTimeLeft={WARNING_THRESHOLD}
            onExpire={showExpirationModal}
          />
          <Typography variant="body1" sx={{ mt: 3 }}>
            Click continue to extend your session.
          </Typography>
        </Box>
      ),
      onConfirm: handleExtendSession,
      confirmText: 'CONTINUE',
    });
  };

  useEffect(() => {
    const checkInactivity = () => {
      if (!isFromLiveListing) {
        // We only discard changes if the listing is not new
        return;
      }

      const now = Date.now();
      const timeElapsed = now - lastActivityRef.current;
      const remainingTime = INACTIVITY_TIMEOUT - timeElapsed;

      if (remainingTime <= WARNING_THRESHOLD && !warningShownRef.current) {
        warningShownRef.current = true;
        handleInactivityWarning();
      }
    };

    const intervalId = setInterval(checkInactivity, 1000);

    const handleFieldUpdate = () => {
      resetTimer();
    };

    window.addEventListener('field-update', handleFieldUpdate);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('field-update', handleFieldUpdate);
    };
  }, [isFromLiveListing]);

  return null;
};

export default ListingInactivityWarning;
